import React from "react";
import { AnswerIcon } from "./Icons";
import Prompt from "./Prompt";

interface SummaryProps {
  question: any;
  currentUser?: any;
  /** Only for the questions whose stored description is a placeholder. */
  prompt?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The answered state for every interactive challenge.
 *
 * Combo's own answered-answer frame — the instruction in a Q bubble, the
 * learner's answer in an A bubble — the same one TextAction, Opinion and
 * FileAction use, so these lessons look like the rest of the platform once
 * answered rather than like a visitor from another app.
 *
 * Widgets supply only the answer content; everything structural lives here, so
 * fourteen screens cannot drift apart.
 */
const Summary: React.FC<SummaryProps> = ({
  question,
  currentUser,
  prompt,
  children,
}) => (
  <div className="ui form assignment-form">
    <Prompt question={question} currentUser={currentUser} fallback={prompt} />

    <div className="form-body">
      <div className="ui image">
        <AnswerIcon />
      </div>
      <div className="content">
        <div className="interactive-answer">{children}</div>
      </div>
    </div>
  </div>
);

/** One labelled part of an answer. The label is optional — several challenges
 *  are a single unlabelled response. */
export const SummaryField: React.FC<{
  label?: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="interactive-answer-field">
    {label && <div className="ix-label">{label}</div>}
    <div>{children}</div>
  </div>
);

/** A set of short values. Chips, not prose — it echoes the picker used to
 *  choose them, and reads as a set. */
export const SummaryPills: React.FC<{ values: string[] }> = ({ values }) => {
  const present = (values || []).filter((v) => (v || "").toString().trim());
  if (!present.length) return <p className="interactive-answer-empty">—</p>;

  return (
    <div className="interactive-answer-pills">
      {present.map((value, i) => (
        <span className="interactive-answer-pill" key={i}>
          {value}
        </span>
      ))}
    </div>
  );
};

/** Free text, preserving the learner's own line breaks. */
export const SummaryText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ whiteSpace: "break-spaces" }}>{children}</p>
);

/** The action row at the foot of an edit view. */
export const Actions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="ix-actions">{children}</div>
);

export default Summary;
